import { Helmet } from 'react-helmet-async';
import "./HackathonsPage.css";
import {
  Trophy,
  Users,
  Zap,
  Calendar,
  MapPin,
  ExternalLink,
  CheckCircle,
  Code2,
  Lightbulb,
  Presentation,
  Star,
} from "lucide-react";

import ibm from "../../assets/logo19.png";
import cern from "../../assets/cern.jpg";
import novon from "../../assets/novon.png";
import angani from "../../assets/angani.jpg";
import IEEE from "../../assets/logo12.png";
import qworld from "../../assets/qworld.jpg";
import oqi from "../../assets/logo15.png";
import actionlab from "../../assets/logo17.png";
import classiq from "../../assets/logo5.png";
import strathmore from "../../assets/logo2.png";

const HackathonsPage = () => {
  const stats = [
    { value: "3+", label: "Hackathons Hosted" },
    { value: "500+", label: "Participants" },
    { value: "10+", label: "Countries Represented" },
  ];

  const steps = [
    {
      step: "01",
      icon: Users,
      color: "teal",
      title: "Form Your Team",
      desc: "Assemble a team of 2–5 quantum enthusiasts from any discipline or compete solo.",
    },
    {
      step: "02",
      icon: Lightbulb,
      color: "purple",
      title: "Register & Get Briefed",
      desc: "Sign up, attend the opening ceremony, and receive the challenge problem set.",
    },
    {
      step: "03",
      icon: Code2,
      color: "blue",
      title: "Build & Hack",
      desc: "Develop your quantum solution over 24–48 hours with mentor support.",
    },
    {
      step: "04",
      icon: Presentation,
      color: "orange",
      title: "Present & Win",
      desc: "Demo your project to expert judges and compete for prizes and recognition.",
    },
  ];

  const partners = [
    { name: "IBM Quantum", logo: ibm },
    { name: "CERN", logo: cern },
    { name: "Classiq", logo: classiq },
    { name: "QWorld", logo: qworld },
    { name: "OQI", logo: oqi },
    { name: "Action Lab", logo: actionlab },
    { name: "IEEE", logo: IEEE },
    { name: "Angani", logo: angani },
    { name: "Novon", logo: novon },
    { name: "Strathmore", logo: strathmore },
    
  ];


  return (
    <div className="hack-page">
      <Helmet>
        <title>Quantum Hackathons | QuantumKE</title>
        <meta name="description" content="Participate in QuantumKE's quantum hackathons. Solve real-world problems using quantum computing and compete with top talent across Kenya and Africa." />
        <link rel="canonical" href="https://quantumke.org/hackathons" />
      </Helmet>
      {/* ===== BANNER ===== */}
      <section className="hack-banner">
        <div className="hack-banner-grid" aria-hidden="true">
          {Array.from({ length: 80 }).map((_, i) => (
            <div key={i} className="hack-banner-cell" />
          ))}
        </div>
        <div className="hack-banner-overlay" />
        <div className="hack-banner-content">
          <h1 className="hack-banner-title">
            <span className="hack-title-gradient">Quantathons</span>
          </h1>
          <p className="hack-banner-subtitle">
            Where quantum ideas meet real-world impact. Build, collaborate, and compete
            at the frontier of quantum computing in Africa.
          </p>
        </div>
      </section>

      {/* ===== HERO / STATS ===== */}
      <section className="hack-hero-section">
        <div className="hack-container">
          <div className="hack-hero-grid">
            {/* Left — Text */}
            <div className="hack-hero-text">
              <h2 className="hack-hero-heading">
                Africa's Gateway to
                <span className="hack-gradient-text"> Quantathons</span>
              </h2>
              <p className="hack-hero-desc">
                QuantumKE partners with global organisations to design and run quantum
                computing hackathons that challenge participants to solve real problems
                using cutting-edge quantum algorithms and hardware. Our hackathons are
                open to students, researchers, developers, and quantum enthusiasts
                across Africa and beyond.
              </p>

              <ul className="hack-hero-highlights">
                {[
                  "Open to all skill levels — beginners welcome",
                  "Mentorship from quantum computing experts",
                  "Access to real quantum hardware via the cloud",
                  "Cash prizes, certificates & global recognition",
                  "Networking with IBM, CERN & leading organisations",
                ].map((item, i) => (
                  <li key={i}>
                    <CheckCircle className="hack-check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — Stats */}
            <div className="hack-hero-stats-panel">
              <div className="hack-stats-inner">
                <span className="hack-stats-badge">Impact So Far</span>
                <div className="hack-stats-grid">
                  {stats.map((s, i) => (
                    <div key={i} className="hack-stat-box">
                      <div className="hack-stat-value">{s.value}</div>
                      <div className="hack-stat-label">{s.label}</div>
                    </div>
                  ))}
                </div>
                <div className="hack-stats-divider" />
                <Trophy className="hack-trophy-icon" />
                <p className="hack-stats-quote">
                  "Empowering African quantum talent through competitive innovation."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURED: QISKIT FALL FEST ===== */}
      <section className="hack-featured-section">
        <div className="hack-container">
          <div className="hack-section-header">
            <h2 className="hack-section-title">Nairobi Qiskit Fall Fest</h2>
            <p className="hack-section-desc">
              Our most successful hackathon — powered by IBM Quantum
            </p>
          </div>

          <div className="hack-featured-card">
            {/* Decorative orbs */}
            <div className="hack-featured-orb hack-orb-1" aria-hidden="true" />
            <div className="hack-featured-orb hack-orb-2" aria-hidden="true" />

            <div className="hack-featured-inner">
              {/* Left: Info */}
              <div className="hack-featured-info">
                <div className="hack-featured-badges">
                  <span className="hack-badge hack-badge-ibm">
                    <img src={ibm} alt="IBM Quantum" className="hack-badge-logo" />
                    In partnership with IBM Quantum
                  </span>
                  <span className="hack-badge hack-badge-annual">Annual Event</span>
                </div>

                <h3 className="hack-featured-title">Nairobi Qiskit Fall Fest</h3>
                <p className="hack-featured-desc">
                  The Qiskit Fall Fest is a global celebration of quantum computing
                  organised annually by IBM. QuantumKE hosts the Nairobi chapter —
                  bringing together Kenya's brightest minds to tackle quantum challenges,
                  learn from IBM experts, and compete for prizes on real quantum hardware.
                  Participants gain hands-on experience with Qiskit, IBM's open-source
                  quantum computing framework, and access to IBM Quantum systems.
                </p>

                <div className="hack-featured-details">
                  <div className="hack-detail-item">
                    <Calendar className="hack-detail-icon" />
                    <span>Annual — October</span>
                  </div>
                  <div className="hack-detail-item">
                    <MapPin className="hack-detail-icon" />
                    <span>Nairobi, Kenya &amp; Online</span>
                  </div>
                  <div className="hack-detail-item">
                    <Users className="hack-detail-icon" />
                    <span>Open to all skill levels</span>
                  </div>
                  <div className="hack-detail-item">
                    <Code2 className="hack-detail-icon" />
                    <span>Qiskit · IBM Quantum Hardware</span>
                  </div>
                </div>

                <a
                  href="https://nairobi-qiskit-fallfest.quantumke.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hack-featured-btn"
                >
                  Visit Hackathon Website
                  <ExternalLink className="hack-btn-icon" />
                </a>
              </div>

              {/* Right: Highlights */}
              <div className="hack-featured-highlights">
                <h4 className="hack-highlights-heading">Why Participate?</h4>
                <ul className="hack-highlights-list">
                  {[
                    { icon: Trophy, text: "Cash prizes & IBM swag" },
                    { icon: Code2, text: "Real IBM Quantum hardware access" },
                    { icon: Users, text: "Mentorship from IBM researchers" },
                    { icon: Star, text: "Global Qiskit community recognition" },
                    { icon: Zap, text: "Fast-track into quantum careers" },
                    { icon: CheckCircle, text: "Official IBM completion certificates" },
                  ].map((item, i) => (
                    <li key={i} className="hack-highlight-item">
                      <div className="hack-highlight-icon-wrap">
                        <item.icon className="hack-highlight-icon" />
                      </div>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== RECAP + HOW IT WORKS (two-column) ===== */}
      <section className="hack-recap-how-section">
        <div className="hack-container">
          <div className="hack-recap-how-grid">

            {/* Left — How It Works */}
            <div className="hack-how-col">
              <h2 className="hack-recap-col-title">How Our Quantathons Work</h2>
              <p className="hack-recap-col-desc">From registration to winning — here's your journey</p>
              <div className="hack-steps-col-grid">
                {steps.map((step, i) => (
                  <div key={i} className="hack-step-card">
                    <span className="hack-step-bg-num">{step.step}</span>
                    <div className={`hack-step-icon-wrap hack-step-${step.color}`}>
                      <step.icon className="hack-step-icon" />
                    </div>
                    <h3 className="hack-step-title">{step.title}</h3>
                    <p className="hack-step-desc">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Video */}
            <div className="hack-recap-col">
              <h2 className="hack-recap-col-title">See It in Action</h2>
              <p className="hack-recap-col-desc">
                Relive the energy, creativity, and quantum breakthroughs from our past quantathons.
              </p>
              <div className="hack-video-wrapper">
                <div className="hack-video-glow" aria-hidden="true" />
                <div className="hack-video-frame">
                  <iframe
                    src="https://www.youtube.com/embed/z71AwEwV-lA?si=h0Wc6dC38msfwxfa"
                    title="QuantumKE Hackathon Recap"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <div className="hack-video-caption">
                  <Zap className="hack-caption-icon" />
                  <span>Nairobi Qiskit Fall Fest — Official Recap</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ===== PARTNERS ===== */}
      <section className="hack-partners-section">
        <div className="hack-container">
          <div className="hack-section-header">
            <h2 className="hack-section-title">Hackathon Partners</h2>
            <p className="hack-section-desc">
              World-class organisations that power our quantum innovation challenges
            </p>
          </div>

          <div className="hack-partners-grid">
            {partners.map((p, i) => (
              <div key={i} className="hack-partner-card">
                <img src={p.logo} alt={p.name} className="hack-partner-logo" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default HackathonsPage;
